import * as z from "zod/v4";
import { VIRTUAL_PROVIDER } from "./dropoff-provider";

// =============================================================================
// Address
// =============================================================================

const DropoffLocationAddressSchema_ = z.object({
  street1: z.string(),
  street2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  country: z.string(),
});
export const DropoffLocationAddressSchema: z.ZodType<DropoffLocationAddress> =
  DropoffLocationAddressSchema_;
export interface DropoffLocationAddress
  extends z.infer<typeof DropoffLocationAddressSchema_> {}

// =============================================================================
// Dropoff Location Result (per-provider discriminated union)
// =============================================================================

const HoursEntrySchema_ = z.object({ open: z.string(), close: z.string() });

const VirtualLocationMetadataSchema_ = z.object({
  phoneNumber: z.string().optional(),
  hours: z
    .object({
      sun: z.array(HoursEntrySchema_).optional(),
      mon: z.array(HoursEntrySchema_).optional(),
      tue: z.array(HoursEntrySchema_).optional(),
      wed: z.array(HoursEntrySchema_).optional(),
      thu: z.array(HoursEntrySchema_).optional(),
      fri: z.array(HoursEntrySchema_).optional(),
      sat: z.array(HoursEntrySchema_).optional(),
    })
    .optional(),
});
export const VirtualLocationMetadataSchema: z.ZodType<VirtualLocationMetadata> =
  VirtualLocationMetadataSchema_;
export interface VirtualLocationMetadata
  extends z.infer<typeof VirtualLocationMetadataSchema_> {}

const dropoffLocationResultBase = {
  id: z.string(),
  name: z.string(),
  address: DropoffLocationAddressSchema,
  distance: z.string().describe("e.g., '1.2 miles', MUST BE IN MILES"),
  geo: z.object({ latitude: z.number(), longitude: z.number() }),
};

const DropoffLocationResultSchema_ = z.discriminatedUnion("provider", [
  z.object({
    ...dropoffLocationResultBase,
    provider: z.literal(VIRTUAL_PROVIDER),
    providerMetadata: VirtualLocationMetadataSchema.optional(),
  }),
]);
export const DropoffLocationResultSchema: z.ZodType<DropoffLocationResult> =
  DropoffLocationResultSchema_;
export type DropoffLocationResult = {} & z.infer<
  typeof DropoffLocationResultSchema_
>;

// =============================================================================
// Get Dropoff Locations Result
// =============================================================================

const GetDropoffLocationsResultSchema_ = z.object({
  locations: z.array(DropoffLocationResultSchema),
});
export const GetDropoffLocationsResultSchema: z.ZodType<GetDropoffLocationsResult> =
  GetDropoffLocationsResultSchema_;
export interface GetDropoffLocationsResult
  extends z.infer<typeof GetDropoffLocationsResultSchema_> {}

// =============================================================================
// Dropoff QR Code Result (per-provider discriminated union)
// =============================================================================

const VirtualQrCodeMetadataSchema_ = z.object({
  dashCode: z.string().optional(),
  expiresAt: z.number().optional(),
  returnVerification: z.literal(true).optional(),
  uniUniOrderNumber: z.string().optional(),
  uniUniTrackingId: z.string().optional(),
  uniUniLabelUrl: z.string().optional(),
});
export const VirtualQrCodeMetadataSchema: z.ZodType<VirtualQrCodeMetadata> =
  VirtualQrCodeMetadataSchema_;
export interface VirtualQrCodeMetadata
  extends z.infer<typeof VirtualQrCodeMetadataSchema_> {}

const dropoffQrCodeResultBase = {
  shipmentGroupId: z.string(),
  externalShipmentId: z.string().optional(),
  qrCode: z.string(),
  qrCodeUrl: z.string(),
  trackingNumber: z.string().optional(),
};

const DropoffQrCodeResultSchema_ = z.discriminatedUnion("provider", [
  z.object({
    ...dropoffQrCodeResultBase,
    provider: z.literal(VIRTUAL_PROVIDER),
    providerMetadata: VirtualQrCodeMetadataSchema,
  }),
]);
export const DropoffQrCodeResultSchema: z.ZodType<DropoffQrCodeResult> =
  DropoffQrCodeResultSchema_;
export type DropoffQrCodeResult = {} & z.infer<
  typeof DropoffQrCodeResultSchema_
>;
